import {FC, useState} from "react";
import {useTranslation} from "react-i18next";
import {useGraphqlLazyQuery, useGraphqlMutation} from "../../amplify/hooks";
import {
  addBoidAssignment as addBoidAssignmentMutation,
  removeBoidAssignment as removeBoidAssignmentMutation,
  lookupClientEntitiesByIds as lookupClientEntitiesByIdsQuery,
  lookupOperationalEntity as lookupOperationalEntityQuery
} from "../../amplify/graphql";
import {useFlags} from "launchdarkly-react-client-sdk";
import {OperationalEntity} from "@common-core/coat-operational-hierarchy-appsync-model";
import {Alert} from "@interstate/components/Alert";
import {Box} from "@interstate/components/Box";
import {Switch} from "@interstate/components/Switch";
import {ModalWithAlert, useAlert} from "../../alerting";
import {
  AddBoidAssignmentData,
  AddBoidAssignmentVariables,
  LookupClientEntitiesByIdsData,
  LookupClientEntitiesByIdsVariables,
  LookupOperationalEntityData,
  LookupOperationalEntityVariables,
  RemoveBoidAssignmentData,
  RemoveBoidAssignmentVariables,
  assertClientEntitiesPresent,
  assertOperationalEntityPresent
} from "../backend";
import {useOperationalEntity} from "../context";
import {statusFor, BoidAssignmentState, createBoidAssignment} from "./model";
import {OnCompleteCallback} from "../../utils";
import "./EditBoidAssignmentsModal.scss";

import {features} from "../../features";
import {BoidEditList} from "./list";
import {
  collectBoidAssignmentStatesFromClientEntities,
  mergeOrphanedAssignmentsIntoStates
} from "./transforms";
import {BoidAssignmentTable, CustomBoidCreationRow} from "./table";
import {useTenancy} from "../operational-hierarchy/hooks/useTenancy";
import {BoidAssignmentInput} from "@common-core/coat-operational-hierarchy-appsync-model";

type BoidAssignmentStateCallback<E = any> = (
  state: BoidAssignmentState,
  event: E
) => void;

export interface EditBoidAssignmentsModalProps {
  boidAssignmentStates: BoidAssignmentState[];
  onComplete: OnCompleteCallback;
}

export const EditBoidAssignmentsModal: FC<EditBoidAssignmentsModalProps> = ({
  boidAssignmentStates,
  onComplete
}) => {
  const {t} = useTranslation();
  const flags = useFlags();
  const [legacyEditorEnabled, setLegacyEditorEnabled] =
    useState<boolean>(false);
  const [working, setWorking] = useState<boolean>(false);
  const {entity, setEntity} = useOperationalEntity();
  const [currentBoidAssignmentStates, setCurrentBoidAssignmentStates] =
    useState<BoidAssignmentState[]>(boidAssignmentStates);
  const [filterText, setFilterText] = useState("");
  const {tenant} = useTenancy(entity.tenantId ?? undefined);
  const isSynthetic = tenant?.synthetic === true;
  const showCustomBoidCreation = isSynthetic && flags[features.dev.entities.enableCreateNewEntity];
  const {alert, clearAlert, createErrorAlert} = useAlert({
    errorTitleKey: "toast.backend-error"
  });

  const [removeBoidAssignment] = useGraphqlMutation<
    RemoveBoidAssignmentData,
    RemoveBoidAssignmentVariables
  >({
    amplifyMutation: removeBoidAssignmentMutation
  });
  const [addBoidAssignment] = useGraphqlMutation<
    AddBoidAssignmentData,
    AddBoidAssignmentVariables
  >({
    amplifyMutation: addBoidAssignmentMutation
  });
  const [lookUpClientEntities] = useGraphqlLazyQuery<
    LookupClientEntitiesByIdsData,
    LookupClientEntitiesByIdsVariables
  >({
    amplifyQuery: lookupClientEntitiesByIdsQuery
  });
  const [lookUpOperationalEntity] = useGraphqlLazyQuery<
    LookupOperationalEntityData,
    LookupOperationalEntityVariables
  >({
    amplifyQuery: lookupOperationalEntityQuery
  });

  const assignBoid: BoidAssignmentStateCallback = async (
    state
  ): Promise<void> => {
    const assignment = createBoidAssignment(state, entity.id);
    setWorking(true);
    clearAlert();
    await addBoidAssignment({variables: {assignment}})
      .then(refreshEntity)
      .then(() => {
        // Add boidFilterContents to the event properties if filterText is non-blank
        const pendoTrackProps: Record<string, any> = {
          ...assignment
        };
        if (filterText.trim() !== "") {
          pendoTrackProps.boidFilterContents = filterText;
        }
        window.pendo?.track("BOID Assigned", pendoTrackProps);
      })
      .catch(createErrorAlert)
      .finally(() => setWorking(false));
  };

  const unassignBoid: BoidAssignmentStateCallback = async (
    state
  ): Promise<void> => {
    const assignment = createBoidAssignment(state, entity.id);
    setWorking(true);
    clearAlert();
    await removeBoidAssignment({variables: {assignment}})
      .then(refreshEntity)
      .then(() => {
        // Add boidFilterContents to the event properties if filterText is non-blank
        const pendoTrackProps: Record<string, any> = {
          ...assignment
        };
        if (filterText.trim() !== "") {
          pendoTrackProps.boidFilterContents = filterText;
        }
        window.pendo?.track("BOID Unassigned", pendoTrackProps);
      })
      .catch(createErrorAlert)
      .finally(() => setWorking(false));
  };

  const updateEntityInContext = (
    entity: OperationalEntity
  ): OperationalEntity => {
    setEntity(entity);
    return entity;
  };

  const collectBoidAssignmentStates = async (
    entity: OperationalEntity
  ): Promise<void> => {
    // Gather the client identifiers from which the current entity is sourced
    const ids = entity.sources
      .map(source => source.sourceId)
      .filter(id => id != null);
    // Collect the boid assignment states and merge with orphaned assignments
    return await lookUpClientEntities({variables: {ids}})
      .then(assertClientEntitiesPresent)
      .then(collectBoidAssignmentStatesFromClientEntities)
      .then(mergeOrphanedAssignmentsIntoStates(entity.boidAssignments))
      .then(setCurrentBoidAssignmentStates);
  };

  const refreshEntity = async (): Promise<void> => {
    return await lookUpOperationalEntity({variables: {id: entity.id}})
      .then(assertOperationalEntityPresent)
      .then(updateEntityInContext)
      .then(collectBoidAssignmentStates);
  };

  const editingComplete = () => {
    const pendoTrackProps: Record<string, any> = {};
    if (filterText.trim() !== "") {
      pendoTrackProps.boidFilterContents = filterText;
    }
    window.pendo?.track("BOID Modal Closed", pendoTrackProps);
    onComplete(!Object.is(boidAssignmentStates, currentBoidAssignmentStates));
  };

  const createCustomBoid = async (input: BoidAssignmentInput): Promise<void> => {
    setWorking(true);
    clearAlert();
    await addBoidAssignment({variables: {assignment: input}})
      .then(refreshEntity)
      .then(() => {
        window.pendo?.track("Custom BOID Created", {...input});
      })
      .catch(createErrorAlert)
      .finally(() => setWorking(false));
  };

  // Helper function to check if there are any orphaned BOIDs
  const hasOrphanedBoids = (entityId: string): boolean => {
    return currentBoidAssignmentStates.some(
      state => statusFor(state, entityId) === "orphaned"
    );
  };

  return (
    <ModalWithAlert
      id={"edit-boid-assignments-modal"}
      data-testid={"edit-boid-assignments-modal"}
      show={true}
      size={"large"}
      header={`${t("entity-detail.boid-assignments")}`}
      alert={alert}
      footer={{
        primary: {
          action: editingComplete,
          label: t("common-actions.finish-editing"),
          isLoading: working
        }
      }}
      onHide={editingComplete}>
      {flags[features.dev.boidEdit.showBoidEditTable] ? (
        <Box
          display={"flex"}
          flexDirection={"row"}
          sx={{
            justifyContent: "flex-end",
            marginBottom: "16px"
          }}>
          <Switch
            id={"legacy-editor-switch"}
            slotProps={{
              switch: {
                dataAttr: {
                  "data-testid": "legacy-editor-switch"
                }
              }
            }}
            label={t("entity-detail.boid-editor.enable-legacy-editor")}
            checked={legacyEditorEnabled}
            onClick={() => {
              // toggle the current state value
              const toggledLegacyEditorEnabled = !legacyEditorEnabled;
              setLegacyEditorEnabled(toggledLegacyEditorEnabled);
              console.dir({
                name: "BOID Assignment Editor Switched",
                metadata: {
                  editor: toggledLegacyEditorEnabled ? "list" : "table"
                }
              });
            }}
          />
        </Box>
      ) : null}
      <Alert
        id={"boid-allocations-info-alert"}
        data-testid={"boid-allocations-info-alert"}
        className={"boid-allocations-alert"}
        type={"caution"}
        title={t("entity-detail.boid-editor.edit-warning.title")}>
        {t("entity-detail.boid-editor.edit-warning.content")}
      </Alert>
      {hasOrphanedBoids(entity.id) && (
        <Alert
          id={"boid-allocations-orphan-alert"}
          data-testid={"boid-allocations-orphan-alert"}
          className={"boid-allocations-alert"}
          type={"info"}
          title={t("entity-detail.boid-editor.orphan-alert.title")}>
          {t("entity-detail.boid-editor.orphan-alert.content")}
        </Alert>
      )}
      {showCustomBoidCreation && (
        <CustomBoidCreationRow
          entityId={entity.id}
          onSubmit={createCustomBoid}
          isLoading={working}
        />
      )}
      {flags[features.dev.boidEdit.showBoidEditTable] ? (
        // Feature flag enabled: Show BoidAssignmentTable by default, BoidEditList when toggle is on
        legacyEditorEnabled ? (
          <BoidEditList
            boidAssignmentStates={currentBoidAssignmentStates}
            onAssignBoid={assignBoid}
            onUnassignBoid={unassignBoid}
            working={working}
          />
        ) : (
          <BoidAssignmentTable
            boids={currentBoidAssignmentStates}
            entityId={entity.id}
            onAssignBoid={assignBoid}
            onUnassignBoid={unassignBoid}
            working={working}
            filterText={filterText}
            onFilterTextChange={setFilterText}
          />
        )
      ) : (
        // Feature flag disabled: Show BoidEditList only
        <BoidEditList
          boidAssignmentStates={currentBoidAssignmentStates}
          onAssignBoid={assignBoid}
          onUnassignBoid={unassignBoid}
          working={working}
        />
      )}
    </ModalWithAlert>
  );
};
